import { Button, Card, Empty, Input, Select, Space, Table, Tag } from 'antd';
import { PlusOutlined, SwapOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import { EducationSessionSelect } from '@/pages/education/components/EducationLookupSelects';

type EnrollmentRow = {
  id: string;
  enrollment_no: string;
  status: string;
  session_id: string;
  session_code?: string | null;
  session_name?: string | null;
  grade_level_id: string;
  grade_code?: string | null;
  grade_name?: string | null;
  first_name?: string;
  last_name?: string;
  current_section_id?: string | null;
  current_section_name?: string | null;
};

const STATUSES = ['draft', 'pending_approval', 'confirmed', 'active', 'withdrawal_pending', 'withdrawn', 'completed', 'cancelled'];

function statusColor(status: string): string {
  switch (status) {
    case 'active':
    case 'confirmed':
      return 'green';
    case 'pending_approval':
    case 'withdrawal_pending':
      return 'orange';
    case 'withdrawn':
    case 'cancelled':
      return 'red';
    case 'completed':
      return 'blue';
    default:
      return 'default';
  }
}

export default function EnrollmentRegisterPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const permissions = useAuthStore((s) => s.user?.permissions || []);
  const canAllocate =
    permissions.includes('education.section.allocate') || permissions.includes('education.admin');
  const status = params.get('status') || undefined;
  const session = params.get('session') || undefined;
  const q = params.get('q') || undefined;
  const allocationView = params.get('view') === 'allocation';

  const query = useQuery({
    queryKey: ['education-enrollments', status, session, q, allocationView ? 'allocation' : 'register'],
    queryFn: () =>
      api.get<EnrollmentRow[]>('/education/enrollments', {
        params: {
          status,
          session_id: session,
          q,
          view: allocationView ? 'allocation' : undefined,
        },
      }),
  });

  const setFilter = (key: string, value?: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next);
  };

  const rows = query.data?.data ?? [];
  const querySuffix = params.toString();

  const openAllocate = (row: EnrollmentRow) => {
    navigate(`/education/enrollments/${row.id}/allocate?${querySuffix}`);
  };

  const openTransfer = (row: EnrollmentRow) => {
    navigate(`/education/enrollments/${row.id}/transfer?${querySuffix}`);
  };

  const openRow = (row: EnrollmentRow) => {
    if (!allocationView) {
      navigate(`/education/enrollments/${row.id}?${querySuffix}`);
      return;
    }
    if (!canAllocate) {
      navigate(`/education/enrollments/${row.id}?${querySuffix}`);
      return;
    }
    if (row.status === 'confirmed' && !row.current_section_id) {
      openAllocate(row);
      return;
    }
    if (row.status === 'active' && row.current_section_id) {
      openTransfer(row);
      return;
    }
    navigate(`/education/enrollments/${row.id}?${querySuffix}`);
  };

  return (
    <EducationPageChrome
      title={
        allocationView
          ? t('sectionsAndTransfersTitle', { defaultValue: 'Sections & transfers' })
          : t('enrollmentsTitle')
      }
      description={
        allocationView
          ? t('sectionsAndTransfersIntro', {
              defaultValue:
                'Allocate a first section for confirmed enrollments, or transfer active students between sections.',
            })
          : t('enrollmentsIntro')
      }
      tags={
        <Tag>
          {rows.length}{' '}
          {allocationView
            ? t('sectionsAndTransfersTitle', { defaultValue: 'Sections & transfers' })
            : t('enrollmentsTitle')}
        </Tag>
      }
      extra={
        allocationView ? (
          <Space wrap>
            <Button onClick={() => navigate('/education/enrollments')}>
              {t('openEnrollments', { defaultValue: 'Enrollment register' })}
            </Button>
          </Space>
        ) : (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate(`/education/enrollments/new?${querySuffix}`)}
          >
            {t('newEnrollment')}
          </Button>
        )
      }
    >
      <Card size="small">
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Space wrap>
            {!allocationView ? (
              <Select
                allowClear
                placeholder={t('status')}
                style={{ minWidth: 200 }}
                value={status}
                options={STATUSES.map((value) => ({ value, label: value }))}
                onChange={(value) => setFilter('status', value)}
              />
            ) : (
              <Select
                allowClear
                placeholder={t('status')}
                style={{ minWidth: 200 }}
                value={status}
                options={[
                  { value: 'confirmed', label: t('needsAllocation', { defaultValue: 'Needs allocation' }) },
                  { value: 'active', label: t('activeCanTransfer', { defaultValue: 'Active (transfer)' }) },
                ]}
                onChange={(value) => setFilter('status', value)}
              />
            )}
            <EducationSessionSelect
              allowClear
              placeholder={t('session')}
              value={session}
              onChange={(value) => setFilter('session', value || undefined)}
              style={{ width: 220 }}
            />
            <Input.Search
              allowClear
              placeholder={t('search')}
              defaultValue={q}
              onSearch={(value) => setFilter('q', value || undefined)}
              style={{ width: 240 }}
            />
          </Space>
          <Table
            rowKey="id"
            loading={query.isLoading}
            dataSource={rows}
            pagination={{ pageSize: 20, showSizeChanger: true }}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    allocationView
                      ? t('sectionsAndTransfersEmpty', {
                          defaultValue: 'No enrollments waiting for section allocation or transfer.',
                        })
                      : t('enrollmentsEmpty', { defaultValue: 'No enrollments yet' })
                  }
                >
                  {!allocationView ? (
                    <Button type="primary" onClick={() => navigate(`/education/enrollments/new?${querySuffix}`)}>
                      {t('newEnrollment')}
                    </Button>
                  ) : null}
                </Empty>
              ),
            }}
            onRow={(row) => ({
              onClick: () => openRow(row),
              style: { cursor: 'pointer' },
            })}
            columns={[
              { title: t('enrollmentNo'), dataIndex: 'enrollment_no', width: 160 },
              {
                title: t('name'),
                render: (_v, row) => `${row.first_name || ''} ${row.last_name || ''}`.trim() || '—',
              },
              {
                title: t('status'),
                dataIndex: 'status',
                width: 160,
                render: (value: string) => <Tag color={statusColor(value)}>{value}</Tag>,
              },
              ...(allocationView
                ? [
                    {
                      title: t('section', { defaultValue: 'Section' }),
                      key: 'section',
                      width: 160,
                      render: (_: unknown, row: EnrollmentRow) =>
                        row.current_section_name || (
                          <Tag color="orange">{t('unallocated', { defaultValue: 'Unallocated' })}</Tag>
                        ),
                    },
                    {
                      title: t('actions'),
                      key: 'actions',
                      width: 220,
                      render: (_: unknown, row: EnrollmentRow) => (
                        <Space
                          wrap
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                        >
                          {canAllocate && row.status === 'confirmed' && !row.current_section_id ? (
                            <Button size="small" type="primary" onClick={() => openAllocate(row)}>
                              {t('allocateSection', { defaultValue: 'Allocate' })}
                            </Button>
                          ) : null}
                          {canAllocate && row.status === 'active' && row.current_section_id ? (
                            <Button size="small" icon={<SwapOutlined />} onClick={() => openTransfer(row)}>
                              {t('transferSection', { defaultValue: 'Transfer' })}
                            </Button>
                          ) : null}
                          <Button size="small" type="link" onClick={() => navigate(`/education/enrollments/${row.id}?${querySuffix}`)}>
                            {t('open', { defaultValue: 'Open' })}
                          </Button>
                        </Space>
                      ),
                    },
                  ]
                : [
                    {
                      title: t('grade', { defaultValue: 'Grade' }),
                      key: 'grade',
                      width: 140,
                      ellipsis: true,
                      render: (_: unknown, row: EnrollmentRow) =>
                        row.grade_name || row.grade_code || '—',
                    },
                    {
                      title: t('session'),
                      key: 'session',
                      width: 180,
                      ellipsis: true,
                      render: (_: unknown, row: EnrollmentRow) =>
                        row.session_code
                          ? row.session_name && row.session_name !== row.session_code
                            ? `${row.session_code} — ${row.session_name}`
                            : row.session_code
                          : row.session_name || '—',
                    },
                  ]),
            ]}
          />
        </Space>
      </Card>
    </EducationPageChrome>
  );
}
